import { createRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { Icon, IconSize } from 'app/components/core/Icon';
import { useHasOverflow } from 'app/lib/hooks';
import { SnackbarVariant } from './constants';
import { useSnackbarContext } from './Snackbar.jsx';
import * as styles from './Snackbar.css';

const cx = classnames.bind(styles);

const icons = {
    [SnackbarVariant.DEFAULT]: null,
    [SnackbarVariant.SUCCESS]: 'checkmark',
    [SnackbarVariant.WARNING]: 'status-warning-solid',
    [SnackbarVariant.ERROR]: 'status-alert-solid'
};

export function SnackbarMessage({ testId, className, children, iconVariant, ...props }) {
    const { variant, setIsTruncated } = useSnackbarContext();
    const icon = iconVariant || icons[variant];
    const classes = cx('message', className);
    const ref = createRef(null);

    const { hasHorizontalOverflow } = useHasOverflow(ref);

    useEffect(() => {
        setIsTruncated(hasHorizontalOverflow);
    }, [hasHorizontalOverflow, setIsTruncated]);
    return (
        <div data-testid={testId} className={classes} title={children} {...props} ref={ref}>
            {icon && <Icon className={styles.icon} size={IconSize.MEDIUM} variant={icon} inline />}
            {children}
        </div>
    );
}

SnackbarMessage.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * SnackbarMessage content.
     */
    children: PropTypes.node,
    /**
     * Custom icon to be shown instead of the default for the snackbar variant.
     */
    iconVariant: PropTypes.string
};
